/**
 * /payroll-l2/compliance-gate/:payRunId — Full compliance gate view
 *
 * Shows all compliance gate results grouped by gate type.
 * Blocked items can be overridden with a reason.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import { api } from "@/lib/api";
import type { ComplianceGateResult } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/compliance-gate_/$payRunId")({
  component: ComplianceGatePage,
});

const GATE_TYPE_LABELS: Record<string, string> = {
  cert_expiry: "Certification Expiry",
  injury_attestation: "Injury Attestation",
  geo_violation: "Geofence Violation",
  face_low_confidence: "Low Face Match Confidence",
  hr_required_unanswered: "Required HR Questions Unanswered",
};

function ResultIcon({ result, blocking }: { result: string; blocking: boolean }) {
  if (result === "pass") return <CheckCircleIcon color="success" fontSize="small" />;
  if (blocking) return <BlockIcon color="error" fontSize="small" />;
  return <WarningIcon color="warning" fontSize="small" />;
}

function ResultChip({ result, blocking }: { result: string; blocking: boolean }) {
  if (result === "pass") return <Chip label="Pass" color="success" size="small" />;
  if (blocking) return <Chip label="Block" color="error" size="small" />;
  return <Chip label="Warn" color="warning" size="small" />;
}

function ComplianceGatePage() {
  const { payRunId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [overrideTarget, setOverrideTarget] = useState<ComplianceGateResult | null>(null);
  const [overrideReason, setOverrideReason] = useState("");

  const { data: results = [], isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "compliance-gate", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/compliance-gate/${payRunId}`);
      return res as ComplianceGateResult[];
    },
  });

  const rerunMut = useMutation({
    mutationFn: async () => {
      return api(`/api/payroll-l2/compliance-gate/${payRunId}/run`, { method: "POST" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "compliance-gate", payRunId] });
    },
  });

  const overrideMut = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      return api(`/api/payroll-l2/compliance-gate/${payRunId}/override/${id}`, {
        method: "POST",
        body: { reason },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "compliance-gate", payRunId] });
      setOverrideTarget(null);
      setOverrideReason("");
    },
  });

  const blockCount = results.filter((r) => r.blocking && r.result !== "pass").length;
  const warnCount = results.filter((r) => !r.blocking && r.result === "fail").length;
  const passCount = results.filter((r) => r.result === "pass").length;

  // Group by check_code prefix (gate type)
  const grouped: Record<string, ComplianceGateResult[]> = {};
  for (const r of results) {
    // gate type is the first part of check_code before the first underscore, or check_code itself
    const gateType = r.check_code.split("_").slice(0, 2).join("_");
    if (!grouped[gateType]) grouped[gateType] = [];
    grouped[gateType].push(r);
  }

  const statusBadge = blockCount > 0
    ? <Chip label={`${blockCount} Blocked`} color="error" />
    : warnCount > 0
    ? <Chip label={`${warnCount} Warnings`} color="warning" />
    : <Chip label="All Clear" color="success" icon={<CheckCircleIcon />} />;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate({ to: "/payroll/pay-runs" })}
        >
          Pay Runs
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Compliance Gate — Pay Run
          </Typography>
          {statusBadge}
        </Box>
        <HelpHint docPath="payroll-l2/compliance-gate.md" label="Compliance gate help" />
      </Box>

      {/* Action strip */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={rerunMut.isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => rerunMut.mutate()}
          disabled={rerunMut.isPending}
        >
          {rerunMut.isPending ? "Running…" : "Re-run Gate"}
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
          {passCount}/{results.length} checks passed
        </Typography>
      </Box>

      {/* Block banner */}
      {blockCount > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<BlockIcon />}>
          <strong>Pay run is blocked</strong> — resolve all compliance issues before calculating.
        </Alert>
      )}

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load compliance results.</Alert>}

      {/* Grouped results */}
      {Object.entries(grouped).map(([gateType, items]) => {
        const hasBlock = items.some((i) => i.blocking && i.result !== "pass");
        const hasWarn = items.some((i) => !i.blocking && i.result !== "pass");
        return (
          <Accordion key={gateType} defaultExpanded={hasBlock || hasWarn}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                <ResultIcon result={hasBlock ? "fail" : hasWarn ? "warn" : "pass"} blocking={hasBlock} />
                <Typography fontWeight={600}>
                  {GATE_TYPE_LABELS[gateType] ?? gateType.replace(/_/g, " ")}
                </Typography>
                <Chip
                  label={`${items.length} item${items.length !== 1 ? "s" : ""}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {items.map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    m: 2,
                    borderColor: item.blocking && item.result !== "pass"
                      ? "error.main"
                      : item.result !== "pass"
                      ? "warning.main"
                      : "success.main",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <ResultChip result={item.result} blocking={item.blocking} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{item.check_name}</Typography>
                      {item.message && (
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          {item.message}
                        </Typography>
                      )}
                      {item.suggested_action && (
                        <Typography variant="body2" color="info.main" mt={0.5}>
                          Action: {item.suggested_action}
                        </Typography>
                      )}
                    </Box>
                    {item.blocking && item.result !== "pass" && (
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => setOverrideTarget(item)}
                      >
                        Override
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {results.length === 0 && !isLoading && (
        <Alert severity="info">No compliance checks recorded. Run the gate first.</Alert>
      )}

      {/* Override dialog */}
      <Dialog open={!!overrideTarget} onClose={() => setOverrideTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Compliance Block</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Overriding a compliance block requires payroll_admin role and a valid business reason.
          </Alert>
          {overrideTarget && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Check: {overrideTarget.check_name}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Business Reason (required)"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
          {overrideMut.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{String((overrideMut.error as Error)?.message)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!overrideReason.trim() || overrideMut.isPending}
            onClick={() =>
              overrideTarget && overrideMut.mutate({ id: overrideTarget.id, reason: overrideReason.trim() })
            }
          >
            {overrideMut.isPending ? "Saving…" : "Override Block"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
